import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { cloneDeep, isString } from 'lodash-es';

import { LogType } from '@vi-iot/ui';
import { logger } from '@vi-iot/ui/services/logger.service';

import { CommissioningActions } from './commissioning.actions';
import {
  COMMISSIONING_STEP,
  DeviceAssignmentResult,
  PAIRING_STEPS,
  ValidateEuidResult,
  ZIGBEE_COORDINATOR_STATUS,
} from './commissioning.model';
import { CommissioningState } from './commissioning.state';

export const initCommissioningState: CommissioningState = {
  feature: {},
  values: {},
  installationId: null,
  gatewaySerial: null,
  featureLoadingState: {
    performing: false,
    performed: false,
    loading: false,
    loaded: false,
    error: null,
  },
  devicePairing: {
    euidValidationResult: null,
    currentCode: null,
    pairingStatus: {
      status: ZIGBEE_COORDINATOR_STATUS.INITIATING,
      step: PAIRING_STEPS.INITIATING,
    },
    activeStep: COMMISSIONING_STEP.START,
    isAlreadyAdded: false,
    isSpecificRoomSelected: false,
    deviceType: null,
    pairedDeviceId: 'deviceId',
  },
  deviceAssignment: {
    room: null,
    heatingCircuit: null,
    rmHeatingCircuit: null,
    ventilation: null,
    channels: [],
    assignmentResult: null,
    validationResult: null,
  },
};

export const commissioningReducer: ActionReducer<CommissioningState, Action> = createReducer(
  initCommissioningState,

  on(
    CommissioningActions.initCommissioning,
    (_state, { isSpecificRoomSelected }): CommissioningState => ({
      ...initCommissioningState,
      devicePairing: {
        ...initCommissioningState.devicePairing,
        isSpecificRoomSelected,
      },
    })
  ),

  on(
    CommissioningActions.resetFeatures,
    (_state): CommissioningState => ({
      ...initCommissioningState,
      featureLoadingState: {
        ...initCommissioningState.featureLoadingState,
        loading: false,
      },
    })
  ),

  on(
    CommissioningActions.resetUpdateFeatureProperty,
    (state): CommissioningState => ({
      ...state,
      featureLoadingState: {
        ...state.featureLoadingState,
        performing: false,
        performed: false,
        error: null,
      },
    })
  ),

  on(
    CommissioningActions.listFeatures,
    (state): CommissioningState => ({
      ...state,
      installationId: null,
      gatewaySerial: null,
      featureLoadingState: {
        ...state.featureLoadingState,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    CommissioningActions.listFeaturesSuccess,
    (state, { feature, installationId, gatewaySerial }): CommissioningState => ({
      ...state,
      feature,
      installationId,
      gatewaySerial,
      featureLoadingState: {
        ...state.featureLoadingState,

        loading: false,
        loaded: true,
      },
    })
  ),

  on(
    CommissioningActions.listFeaturesError,
    (state): CommissioningState => ({
      ...state,
      feature: {},
      featureLoadingState: {
        ...state.featureLoadingState,
        loading: false,
        loaded: true,
        error: 'Some error occured',
      },
    })
  ),

  on(
    CommissioningActions.getFeature,
    (_state): CommissioningState => ({
      ...initCommissioningState,
    })
  ),
  on(
    CommissioningActions.getFeatureSuccess,
    (_state): CommissioningState => ({
      ...initCommissioningState,
    })
  ),
  on(
    CommissioningActions.getFeatureError,
    (_state): CommissioningState => ({
      ...initCommissioningState,
    })
  ),

  on(
    CommissioningActions.liveUpdateFeature,
    (state, { featureName, feature }): CommissioningState => {
      logger.log('old value', LogType.info);
      logger.log(state.feature[featureName]?.properties, LogType.info);
      logger.log('---------------------------------', LogType.info);

      state.feature[featureName] = { ...feature };

      return {
        ...state,
      };
    }
  ),

  on(
    CommissioningActions.updateFeatureProperty,
    (state, { featureName, value, propertyName, commandName }): CommissioningState => {
      const feature = state.feature[featureName];

      const featuresValues = { ...state.values };
      let featureValue = featuresValues[featureName];
      const oldFeatureValue = {};
      if (isString(propertyName)) {
        if (!feature.properties[propertyName as string]) {
          feature.properties[propertyName as string] = {
            value: '',
          };
        }
        oldFeatureValue[propertyName as string] = cloneDeep(
          feature.properties[propertyName as string].value
        );
      } else if (propertyName && !commandName) {
        (propertyName as string[]).forEach((property) => {
          oldFeatureValue[property] = cloneDeep(feature.properties[property].value);
        });
      }
      if (featureValue) {
        featureValue = {
          ...featureValue,
          ...oldFeatureValue,
        };
      } else {
        featureValue = { ...oldFeatureValue };
      }
      featuresValues[featureName] = featureValue;

      // Set new value
      if (isString(propertyName)) {
        if (!feature.properties[propertyName as string]) {
          feature.properties[propertyName as string] = {
            value: '',
          };
        }
        feature.properties[propertyName as string].value = cloneDeep(value);
      } else if (propertyName && !commandName) {
        (propertyName as string[]).forEach((property, i) => {
          feature.properties[property].value = cloneDeep(value[i]);
        });
      }
      state.feature[featureName] = { ...feature };

      return {
        ...state,
        featureLoadingState: {
          ...state.featureLoadingState,
          performing: true,
          performed: false,
        },
        values: featuresValues,
      };
    }
  ),

  on(
    CommissioningActions.updateFeaturePropertySuccess,
    (state, { isAlreadyAdded, deviceId }): CommissioningState => ({
      ...state,
      featureLoadingState: {
        ...state.featureLoadingState,
        performing: false,
        performed: true,
      },
      devicePairing: {
        ...state.devicePairing,
        isAlreadyAdded,
        pairedDeviceId: deviceId,
      },
    })
  ),

  on(
    CommissioningActions.updateFeaturePropertyError,
    (state, { featureName, property, error }): CommissioningState => {
      // Restore old value
      const _feature = state.feature[featureName];
      if (isString(property)) {
        _feature.properties[property as string].value = cloneDeep(
          state.values[featureName][property as string]
        );
      } else if (property) {
        (property as string[]).forEach((propertyEntry) => {
          _feature.properties[propertyEntry].value = cloneDeep(
            state.values[featureName][propertyEntry]
          );
        });
      }
      state.feature[featureName] = {
        ..._feature,
      };

      return {
        ...state,
        featureLoadingState: {
          ...state.featureLoadingState,
          performing: false,
          performed: false,
          error,
        },
      };
    }
  ),

  on(
    CommissioningActions.updatePairingStatusSuccess,
    (state): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        activeStep: COMMISSIONING_STEP.PAIRING_SUCCESS,
      },
    })
  ),

  on(
    CommissioningActions.updatePairingStatusError,
    (state, { step, status }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        activeStep: step ? step : state.devicePairing.activeStep,
        pairingStatus: {
          ...state.devicePairing.pairingStatus,
          status: status ? status : state.devicePairing.pairingStatus.status,
        },
      },
    })
  ),

  on(
    CommissioningActions.setPairingStatus,
    (state, { status, step }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        pairingStatus: {
          status,
          step,
        },
      },
    })
  ),

  on(
    CommissioningActions.setActiveStep,
    (state, { step }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        activeStep: step,
      },
    })
  ),

  on(
    CommissioningActions.setConnectingPairingStatus,
    (state, { status, step }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        pairingStatus: {
          status,
          step,
        },
      },
    })
  ),

  on(
    CommissioningActions.setCode,
    (state, { code }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        currentCode: code,
      },
    })
  ),

  on(
    CommissioningActions.changeDeviceType,
    (state, { deviceType }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        deviceType,
      },
    })
  ),

  on(
    CommissioningActions.resetEuidValidation,
    (state): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        euidValidationResult: null,
      },
    })
  ),

  on(
    CommissioningActions.verifyEuid.success,
    (state): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        euidValidationResult: ValidateEuidResult.Success,
      },
    })
  ),

  on(
    CommissioningActions.verifyEuid.error,
    (state, { error }): CommissioningState => ({
      ...state,
      devicePairing: {
        ...state.devicePairing,
        euidValidationResult: error,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.room,
    (state, { data }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...state.deviceAssignment,
        room: data,
        assignmentResult: null,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.heatingCircuit,
    (state, { data }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...state.deviceAssignment,
        heatingCircuit: data,
        assignmentResult: null,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.remoteHeatingCircuit,
    (state, { data }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...state.deviceAssignment,
        rmHeatingCircuit: data,
        assignmentResult: null,
      },
    })
  ),
  on(
    CommissioningActions.deviceAssign.ventilation,
    (state, { data }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...state.deviceAssignment,
        ventilation: data,
        assignmentResult: null,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.channels,
    (state, { data }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...state.deviceAssignment,
        channels: data,
        assignmentResult: null,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.success,
    (state, { validationData }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...initCommissioningState.deviceAssignment,
        assignmentResult: DeviceAssignmentResult.Success,
        validationResult: validationData,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.error,
    (state, { validationData }): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...initCommissioningState.deviceAssignment,
        assignmentResult: DeviceAssignmentResult.Error,
        validationResult: validationData,
      },
    })
  ),

  on(
    CommissioningActions.deviceAssign.reset,
    (state): CommissioningState => ({
      ...state,
      deviceAssignment: {
        ...initCommissioningState.deviceAssignment,
      },
    })
  ),

  on(
    CommissioningActions.resetCommissioningState,
    (): CommissioningState => ({
      ...initCommissioningState,
    })
  )
);
